import { toast } from "sonner";
import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { SERVICES } from "@/data/services";
import { ArrowRight, CheckCircle2, Loader2 } from "lucide-react";

const inputCls =
  "w-full rounded-lg border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 outline-none transition-colors focus:border-brand-red focus:ring-4 focus:ring-brand-red/10";

export default function QuoteForm({ defaultService = "", defaultMessage = "", compact = false }) {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    suburb: "",
    service: defaultService || "",
    how_heard: "",
    message: defaultMessage || "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (defaultMessage) setForm((f) => ({ ...f, message: defaultMessage }));
  }, [defaultMessage]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/quotes", form);
      setDone(true);
      toast.success("Quote request received", {
        description: "We'll be in touch shortly - usually the same day.",
      });
    } catch (err) {
      toast.error("Could not send your request", { description: formatApiError(err) });
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div
        className="flex flex-col items-center justify-center gap-4 rounded-2xl border border-emerald-200 bg-emerald-50 px-8 py-14 text-center"
        data-testid="quote-success"
      >
        <CheckCircle2 className="h-10 w-10 text-emerald-600" />
        <p className="font-display text-xl font-extrabold text-slate-900">Thanks - request received.</p>
        <p className="max-w-sm text-sm text-slate-600">
          We'll review your details and get back to you shortly. Need urgent glass replacement? Call us now on{" "}
          <a href="tel:+61485032854" className="font-bold text-brand-red underline-offset-4 hover:underline">
            0485 032 854
          </a>
          .
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="space-y-4" data-testid="quote-form">
      <div className={`grid gap-4 ${compact ? "" : "sm:grid-cols-2"}`}>
        <input
          required
          minLength={2}
          placeholder="Your name"
          value={form.name}
          onChange={set("name")}
          className={inputCls}
          data-testid="quote-input-name"
        />
        <input
          required
          type="email"
          placeholder="Email"
          value={form.email}
          onChange={set("email")}
          className={inputCls}
          data-testid="quote-input-email"
        />
        <input
          required
          type="tel"
          placeholder="Mobile"
          value={form.phone}
          onChange={set("phone")}
          className={inputCls}
          data-testid="quote-input-phone"
        />
        <input
          required
          placeholder="Suburb"
          value={form.suburb}
          onChange={set("suburb")}
          className={inputCls}
          data-testid="quote-input-suburb"
        />
        <select
          required
          value={form.service}
          onChange={set("service")}
          className={`${inputCls} appearance-none ${form.service ? "" : "text-slate-400"}`}
          data-testid="quote-input-service"
        >
          <option value="" disabled className="bg-white">
            Service needed
          </option>
          {SERVICES.map((s) => (
            <option key={s.slug} value={s.name} className="bg-white text-slate-900">
              {s.name}
            </option>
          ))}
          <option value="Not sure / something else" className="bg-white text-slate-900">
            Not sure / something else
          </option>
        </select>
        <select
          value={form.how_heard}
          onChange={set("how_heard")}
          className={`${inputCls} appearance-none ${form.how_heard ? "" : "text-slate-400"}`}
          data-testid="quote-input-how-heard"
        >
          <option value="" className="bg-white">
            How did you hear about us?
          </option>
          {["Google search", "Facebook / Instagram", "Word of mouth", "Returning customer", "Other"].map((o) => (
            <option key={o} value={o} className="bg-white text-slate-900">
              {o}
            </option>
          ))}
        </select>
      </div>
      <textarea
        rows={4}
        placeholder="Tell us about the job - a brief description and some measurements help"
        value={form.message}
        onChange={set("message")}
        className={inputCls}
        data-testid="quote-input-message"
      />
      <button
        type="submit"
        disabled={submitting}
        className="btn-red w-full disabled:opacity-60"
        data-testid="quote-submit-btn"
      >
        {submitting ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" /> Sending...
          </>
        ) : (
          <>
            Request a free quote
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </>
        )}
      </button>
      <p className="text-center text-xs text-slate-500">Free quotes, no obligation. We usually reply the same day.</p>
    </form>
  );
}
